import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Alert,
  DialogTitle,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  Paper,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Switch,
  FormControlLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import './CSS/Projects.css';
import { API_URL } from '../apiConfig'; 

const MAX_NAME_LENGTH = 50;
const MAX_SCENE_DESCRIPTION_LENGTH = 500;

const statusColors = {
  ongoing: '#4caf50',
  pending: '#ff9800',
  completed: '#f44336',
  cancelled: '#9e9e9e'
};

const StatusLabel = ({ status }) => {
  const color = statusColors[status.toLowerCase()] || '#000';
  return (
    <Box component="span" sx={{
      backgroundColor: color,
      color: '#fff',
      borderRadius: '4px',
      padding: '2px 8px',
      fontWeight: 'bold',
      display: 'inline-block'
    }}>
      {status}
    </Box>
  );
};

const deepfakeTypes = ['Face', 'Head'];
const audioOptions = ['Yes', 'No'];

const deepfakeExplanations = {
  Face: 'Face deepfake involves replacing or altering the facial features of the target in the video. This technique is suitable for scenarios where only facial expressions or identity need to be altered.',
  Head: 'Head deepfake extends beyond the face to include the entire head, making it suitable for scenarios where head movements and interactions need to be altered along with the facial features.'
};

const deepfakeLimitations = {
  Face: 'Limitations: Face deepfakes may not perform well if the subject’s head moves significantly or interacts with objects in the scene. It works best with relatively static head positions.',
  Head: 'Limitations: Head deepfakes require more data and processing power. They may not perform as well if the background is complex or if there are significant interactions with the environment.'
};

const bodyDoubleExplanation = 'Using a body double involves substituting another person for the original actor in a scene. This is typically used when the original actor is unavailable or to perform stunts or other actions the original actor cannot perform.';
const deageExplanation = 'If not using a body double, de-aging, aging, or applying cosmetics to the same person involves digitally altering their appearance to look younger, older, or to apply cosmetic changes. This technique is suitable for continuity or to match a specific look required for the scene.';

const datasetExplanation = 'The dataset is a collection of images and videos used to recreate the person. Choose a dataset that matches the person to be deepfaked. If you do not have a dataset, you can select "No dataset for the person".';

const Projects = ({ darkMode }) => {
  const [projects, setProjects] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isCardView, setIsCardView] = useState(true);
  const steps = ['Basic Information', 'Dataset Details', 'Deepfake Details', 'Additional Details'];
  const [showWarning, setShowWarning] = useState(false);
  const [userRole, setUserRole] = useState('');

  const [newProject, setNewProject] = useState({
    name: '',
    deadline: '',
    dataset_uuid: '',
    model_uuid: '',
    deepfake_type: [],
    body_double_usage: '',
    deage_or_cosmetics_reason: '',
    clip_length: 0,
    scene_description: '',
    audio_deepfake: '',
    audio_model_uuid: '',
    desired_resolution: ''
  });

  const [selectedProject, setSelectedProject] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [projectDetailsOpen, setProjectDetailsOpen] = useState(false);
  const [showCancelledProjects, setShowCancelledProjects] = useState(false);
  const [csrfToken, setCsrfToken] = useState('');

  const fetchCsrfToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/get-csrf-token`, { withCredentials: true });
      setCsrfToken(response.data.csrf_token);
    } catch (error) {
      console.error('Error fetching CSRF token:', error);
    }
  };

  const fetchProjectInfo = async () => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const response = await axios.get(`${API_URL}/api/project-info`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDatasets(response.data.datasets);
        setModels(response.data.models);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching project info:', err);
        if (err.response && err.response.status === 403) {
          setError('You need to verify your email to access this page. Go to the settings page to resolve this.');
        } else {
          setError('Failed to fetch datasets or models');
        }
        setLoading(false);
      }
    } else {
      setError('No access token found');
      setLoading(false);
    }
  };

  const fetchUserDetails = async () => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const userInfoResponse = await axios.get(`${API_URL}/api/user_details`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserRole(userInfoResponse.data.role);
      } catch (err) {
        setError('Failed to fetch user details');
      }
    } else {
      setError('No access token found');
    }
  };

  const fetchProjects = async () => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const response = await axios.get(`${API_URL}/api/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProjects(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching projects:', err);
        if (err.response && err.response.status === 403) {
          setError('You need to verify your email to access this page.');
        } else {
          setError('Failed to fetch projects');
        }
        setLoading(false);
      }
    } else {
      setError('No access token found');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCsrfToken();
    fetchProjects();
    fetchProjectInfo();
    fetchUserDetails();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === 'name') {
      newValue = value.slice(0, MAX_NAME_LENGTH);
    }

    if (name === 'scene_description') {
      newValue = value.slice(0, MAX_SCENE_DESCRIPTION_LENGTH);
    }

    setNewProject(prevState => {
      const clipLengthValue = name === 'clip_length' ? Math.min(5, Math.max(0, value)) : newValue;
      setShowWarning(clipLengthValue === 5);
      return {
        ...prevState,
        [name]: clipLengthValue
      };
    });
  };

  const handleDeepfakeTypeChange = (e) => {
    const { value } = e.target;
    setNewProject(prevState => ({
      ...prevState,
      deepfake_type: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length > maxLength) {
      return text.substring(0, maxLength - 3) + '...';
    }
    return text;
  };

  const handleClipLengthChange = (delta) => {
    setNewProject(prevState => {
      const newLength = Math.min(5, Math.max(0, prevState.clip_length + delta));
      setShowWarning(newLength === 5);
      return {
        ...prevState,
        clip_length: newLength
      };
    });
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    if (!newProject.name || !newProject.deadline || newProject.clip_length <= 0 || !newProject.scene_description || !newProject.dataset_uuid || !newProject.model_uuid || !newProject.deepfake_type.length || !newProject.body_double_usage || !newProject.audio_deepfake) {
      setError('All fields are required and must be filled in correctly.');
      return;
    }

    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        let payload = {
          ...newProject,
          status: 'Pending',
          deepfake_type: newProject.deepfake_type.join(',')
        };

        if (newProject.model_uuid === "no_model" && newProject.desired_resolution) {
          payload.new_model_request = newProject.desired_resolution;
        }

        await axios.post(`${API_URL}/api/projects`, payload, {
          headers: { Authorization: `Bearer ${token}`, 'X-CSRFToken': csrfToken },
          withCredentials: true
        });
        setOpen(false);
        fetchProjects();
      } catch (err) {
        setError('Failed to create project');
      }
    }
  };

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setProjectDetailsOpen(true);
  };

  const handleCloseProjectDetails = () => {
    setSelectedProject(null);
    setProjectDetailsOpen(false);
    setEditMode(false);
  };

  const handleEditProject = async () => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        await axios.put(`${API_URL}/api/projects/${selectedProject.project_uuid}`, selectedProject, {
          headers: { Authorization: `Bearer ${token}`, 'X-CSRFToken': csrfToken },
          withCredentials: true
        });
        fetchProjects();
        setEditMode(false);
      } catch (err) {
        setError('Failed to update project');
      }
    }
  };

  const handleCancelProject = async () => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        await axios.post(`${API_URL}/api/projects/${selectedProject.project_uuid}/cancel`, {}, {
          headers: { Authorization: `Bearer ${token}`, 'X-CSRFToken': csrfToken },
          withCredentials: true
        });
        fetchProjects();
        handleCloseProjectDetails();
      } catch (err) {
        setError('Failed to cancel project');
      }
    }
  };

  const handleProjectInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedProject(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const isNextDisabled = () => {
    switch (activeStep) {
      case 0:
        return !newProject.name || !newProject.deadline;
      case 1:
        return !newProject.dataset_uuid || !newProject.body_double_usage || (newProject.body_double_usage === 'No' && !newProject.deage_or_cosmetics_reason);
      case 2:
        return !newProject.deepfake_type.length || !newProject.model_uuid || (newProject.model_uuid === "no_model" && !newProject.desired_resolution) || !newProject.audio_deepfake;
      case 3:
        return !newProject.clip_length || !newProject.scene_description;
      default:
        return true;
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label={`Project Name (${newProject.name.length}/${MAX_NAME_LENGTH})`}
              type="text"
              fullWidth
              value={newProject.name}
              onChange={handleInputChange}
              inputProps={{ maxLength: MAX_NAME_LENGTH }}
            />
            <TextField
              margin="dense"
              name="deadline"
              label="Deadline"
              type="date"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              value={newProject.deadline}
              onChange={handleInputChange}
            />
          </>
        );
      case 1:
        return (
          <>
            <TextField
              margin="dense"
              name="dataset_uuid"
              label="Dataset"
              select
              fullWidth
              value={newProject.dataset_uuid}
              onChange={handleInputChange}
            >
              {datasets.map((dataset) => (
                <MenuItem key={dataset.dataset_uuid} value={dataset.dataset_uuid}>
                  {`${dataset.name} (Age: ${dataset.age})`}
                </MenuItem>
              ))}
              <MenuItem value="no_dataset">No dataset for the person</MenuItem>
            </TextField>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" color="textPrimary"><strong>Dataset Explanation:</strong> {datasetExplanation}</Typography>
            </Box>
            <TextField
              margin="dense"
              name="body_double_usage"
              label="Body Double Usage"
              select
              fullWidth
              value={newProject.body_double_usage}
              onChange={handleInputChange}
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" color="textPrimary"><strong>Body Double Explanation:</strong> {bodyDoubleExplanation}</Typography>
            </Box>
            {newProject.body_double_usage === 'No' && (
              <>
                <TextField
                  margin="dense"
                  name="deage_or_cosmetics_reason"
                  label="De-aging, Aging, or Cosmetics Reasons"
                  select
                  fullWidth
                  value={newProject.deage_or_cosmetics_reason}
                  onChange={handleInputChange}
                >
                  <MenuItem value="De-aging">De-aging</MenuItem>
                  <MenuItem value="Aging">Aging</MenuItem>
                  <MenuItem value="Cosmetics">Cosmetics</MenuItem>
                </TextField>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1" color="textPrimary"><strong>De-aging, Aging, or Cosmetics Explanation:</strong> {deageExplanation}</Typography>
                </Box>
              </>
            )}
          </>
        );
      case 2:
        return (
          <>
            <TextField
              margin="dense"
              name="deepfake_type"
              label="Deepfake Type"
              select
              fullWidth
              multiple
              value={newProject.deepfake_type}
              onChange={handleDeepfakeTypeChange}
            >
              {deepfakeTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
            {newProject.deepfake_type.map(type => (
              <Box key={type} sx={{ mt: 2 }}>
                <Typography variant="body1" color="textPrimary"><strong>{type} Deepfake Explanation:</strong> {deepfakeExplanations[type]}</Typography>
                <Typography variant="body2" color="textSecondary">{deepfakeLimitations[type]}</Typography>
              </Box>
            ))}
            <TextField
              margin="dense"
              name="model_uuid"
              label="Model"
              select
              fullWidth
              value={newProject.model_uuid}
              onChange={handleInputChange}
            >
              <MenuItem value="no_model">I don't have a model</MenuItem>
              {models
                .filter(model =>
                  model.model_type === "video" &&
                  newProject.deepfake_type.map(type => type.toLowerCase()).includes(model.model_type_specific)
                )
                .map((model) => (
                  <MenuItem key={model.model_uuid} value={model.model_uuid}>
                    {`${model.name} - (Resolution: ${model.resolution})`}
                  </MenuItem>
                ))
              }
            </TextField>
            {newProject.model_uuid === "no_model" && (
              <TextField
                margin="dense"
                name="desired_resolution"
                label="Desired Resolution"
                select
                fullWidth
                value={newProject.desired_resolution}
                onChange={handleInputChange}
              >
                <MenuItem value="256x256">256x256</MenuItem>
                <MenuItem value="512x512">512x512</MenuItem>
                <MenuItem value="1024x1024">1024x1024</MenuItem>
              </TextField>
            )}
            {newProject.model_uuid === "no_model" && newProject.desired_resolution && (
              <Box sx={{ mt: 1 }}>
                <Alert severity="warning" sx={{ backgroundColor: '#fff3e0', color: '#663c00' }}>
                  <Typography variant="body2">
                    {newProject.desired_resolution === "256x256" && "Training a 256x256 model from scratch will take approximately 3-4 days."}
                    {newProject.desired_resolution === "512x512" && "Training a 512x512 model from scratch will take approximately 7 days."}
                    {newProject.desired_resolution === "1024x1024" && "Training a 1024x1024 model from scratch will take a minimum of 14 days."}
                    {" You will need to provide your own training data."}
                  </Typography>
                </Alert>
              </Box>
            )}
            {newProject.model_uuid && newProject.model_uuid !== "no_model" && (
              <Box sx={{ mt: 1 }}>
                <Alert severity="warning" sx={{ backgroundColor: '#fff3e0', color: '#663c00' }}>
                  <Typography variant="body2">
                    Model trained on: {models.find(m => m.model_uuid === newProject.model_uuid)?.name_A} and {models.find(m => m.model_uuid === newProject.model_uuid)?.name_B}
                  </Typography>
                </Alert>
              </Box>
            )}
            <TextField
              margin="dense"
              name="audio_deepfake"
              label="Audio Deepfake"
              select
              fullWidth
              value={newProject.audio_deepfake}
              onChange={handleInputChange}
            >
              {audioOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            {newProject.audio_deepfake === 'Yes' && (
              <>
                <TextField
                  margin="dense"
                  name="audio_model_uuid"
                  label="Audio Model"
                  select
                  fullWidth
                  value={newProject.audio_model_uuid}
                  onChange={handleInputChange}
                >
                  <MenuItem value="no_audio_model">I don't have an audio model</MenuItem>
                  {models
                    .filter(model => model.model_type === "audio")
                    .map((model) => (
                      <MenuItem key={model.model_uuid} value={model.model_uuid}>
                        {`${model.name} - (Sample Rate: ${model.sample_rate})`}
                      </MenuItem>
                    ))
                  }
                </TextField>
                {newProject.audio_model_uuid && newProject.audio_model_uuid !== "no_audio_model" && (
                  <Box sx={{ mt: 1 }}>
                    <Alert severity="warning" sx={{ backgroundColor: '#fff3e0', color: '#663c00' }}>
                      <Typography variant="body2" color="textSecondary">
                        Audio model trained on: {models.find(m => m.model_uuid === newProject.audio_model_uuid)?.name_A}
                      </Typography>
                    </Alert>
                  </Box>
                )}
              </>
            )}
          </>
        );
      case 3:
        return (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Button
                variant="outlined"
                onClick={() => handleClipLengthChange(-1)}
                sx={{ minWidth: '40px' }}>
                -
              </Button>
              <TextField
                margin="dense"
                name="clip_length"
                label="Clip Length (minutes)"
                type="number"
                fullWidth
                value={newProject.clip_length}
                onChange={handleInputChange}
                inputProps={{ min: 0, max: 5 }}
                sx={{ mx: 2, maxWidth: '100px' }}
              />
              <Button
                variant="outlined"
                onClick={() => handleClipLengthChange(1)}
                sx={{ minWidth: '40px' }}>
                +
              </Button>
            </Box>
            {showWarning && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                The maximum length for a scene is 5 minutes. If you need a longer scene, please send an inquiry in the Communication page to the archiveME team.
              </Alert>
            )}
            <TextField
              margin="dense"
              name="scene_description"
              label={`Scene Description (${newProject.scene_description.length}/${MAX_SCENE_DESCRIPTION_LENGTH})`}
              type="text"
              fullWidth
              multiline
              rows={4}
              value={newProject.scene_description}
              onChange={handleInputChange}
              inputProps={{ maxLength: MAX_SCENE_DESCRIPTION_LENGTH }}
              sx={{ mt: 2 }}
            />
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2">
                  Please provide detailed information about the scene to be deepfaked, including lighting settings, camera angles, and any other relevant details.
                </Typography>
              </Alert>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning" sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2">
                  This is not an order but a project request. We will review the project before proceeding forward.
                </Typography>
              </Alert>
            </Box>
          </>
        );
      default:
        return 'Unknown step';
    }
  };

  if (loading) {
    return (
      <div className="p-6 pt-16">
        <Typography variant="h6">Loading...</Typography>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 pt-16">
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  const ongoingProjects = projects.filter(project => ['Ongoing', 'Pending'].includes(project.status));
  const completedProjects = projects.filter(project => project.status === 'Completed');
  const cancelledProjects = projects.filter(project => project.status === 'Cancelled');

  const toggleView = () => {
    setIsCardView(!isCardView);
  };

  return (
    <div className="p-6 pt-16">
      <Typography variant="h4" gutterBottom>
        Projects
      </Typography>

      <FormControlLabel
        control={<Switch checked={isCardView} onChange={toggleView} />}
        label={isCardView ? 'Card View' : 'Table View'}
      />

      {isCardView ? (
        <>
          {/* Ongoing Projects */}
          <Typography variant="h6" gutterBottom>
            Ongoing Projects
          </Typography>
          <Grid container spacing={3}>
            {ongoingProjects.map(project => (
              <Grid item xs={12} sm={6} md={4} key={project.project_uuid}>
                <Card className="projects-card team-member-card" onClick={() => handleProjectClick(project)}>
                  <CardContent className="projects-card-content">
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar className="projects-avatar" sx={{ bgcolor: 'primary.main', mr: 2 }}>
                        {project.name.charAt(0)}
                      </Avatar>
                      <Typography variant="h5">{project.name}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography sx={{ mb: 1.5 }} color="text.secondary">
                        {truncateText(project.scene_description, 30)}
                      </Typography>
                    </Box>
                  </CardContent>
                  <Box className="projects-card-footer" sx={{ textAlign: 'left' }}>
                    <Typography variant="body2">
                      <StatusLabel status={project.status} />
                    </Typography>
                    <Typography variant="body2">
                      Due Date: {new Date(project.deadline).toLocaleDateString()}
                    </Typography>
                    <Box className="projects-chip" sx={{ mt: 2, display: 'flex', flexWrap: 'wrap' }}>
                      {project.tags ? project.tags.split(',').map(tag => (
                        <Chip key={tag} label={tag.trim()} className="projects-chip" sx={{ mr: 1, mb: 1 }} />
                      )) : null}
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}

            {userRole !== 'Viewer' && (
              <Grid item xs={12} sm={6} md={4}>
                <Card className="projects-card team-member-card" onClick={() => setOpen(true)}>
                  <CardContent className="projects-card-content">
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar className="projects-avatar" sx={{ bgcolor: 'primary.main', mr: 2 }}>
                        +
                      </Avatar>
                      <Typography variant="h5" >New Project</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography sx={{ mb: 1.5 }} color="text.secondary">
                        Click here to create a new project.
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* Completed Projects */}
          <Typography variant="h6" gutterBottom sx={{ mt: 5 }}>
            Completed Projects
          </Typography>
          <Grid container spacing={3}>
            {completedProjects.map(project => (
              <Grid item xs={12} sm={6} md={4} key={project.project_uuid}>
                <Card className="projects-card team-member-card" onClick={() => handleProjectClick(project)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar className="projects-avatar" sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                        {project.name.charAt(0)}
                      </Avatar>
                      <Typography variant="h5">{project.name}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography sx={{ mb: 1.5 }} color="text.secondary">
                        {truncateText(project.scene_description, 60)}
                      </Typography>
                      <Typography variant="body2">
                        <StatusLabel status={project.status} />
                      </Typography>
                      <Typography variant="body2">
                        Completed on: {new Date(project.deadline).toLocaleDateString()}
                      </Typography>
                      <Box className="projects-chip" sx={{ mt: 2, display: 'flex', flexWrap: 'wrap' }}>
                        {project.tags ? project.tags.split(',').map(tag => (
                          <Chip key={tag} label={tag.trim()} className="projects-chip" sx={{ mr: 1, mb: 1 }} />
                        )) : null}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Cancelled Projects */}
          <Box sx={{ mt: 5 }}>
            <Button onClick={() => setShowCancelledProjects(!showCancelledProjects)}>
              {showCancelledProjects ? 'Hide Cancelled Projects' : 'Show Cancelled Projects'}
            </Button>
            {showCancelledProjects && (
              <>
                <Typography variant="h6" gutterBottom>
                  Cancelled Projects
                </Typography>
                <Grid container spacing={3}>
                  {cancelledProjects.map(project => (
                    <Grid item xs={12} sm={6} md={4} key={project.project_uuid}>
                      <Card className="projects-card team-member-card" onClick={() => handleProjectClick(project)}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar className="projects-avatar" sx={{ bgcolor: 'error.main', mr: 2 }}>
                              {project.name.charAt(0)}
                            </Avatar>
                            <Typography variant="h5">{project.name}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'left' }}>
                            <Typography sx={{ mb: 1.5 }} color="text.secondary">
                              {truncateText(project.scene_description, 60)}
                            </Typography>
                            <Typography variant="body2">
                              <StatusLabel status={project.status} />
                            </Typography>
                            <Typography variant="body2">
                              Cancelled on: {new Date(project.updated_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Box>
        </>
      ) : (
        <>
          {/* Table View */}
          <Typography variant="h6" gutterBottom>
            Projects
          </Typography>
          <TableContainer component={Paper} className="table-container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Scene Description</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Tags</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.map(project => (
                  <TableRow key={project.project_uuid} onClick={() => handleProjectClick(project)} style={{ cursor: 'pointer' }}>
                    <TableCell>{project.name}</TableCell>
                    <TableCell><StatusLabel status={project.status} /></TableCell>
                    <TableCell>{truncateText(project.scene_description, 60)}</TableCell>
                    <TableCell>{new Date(project.deadline).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {project.tags ? project.tags.split(',').map(tag => (
                        <Chip key={tag} label={tag.trim()} className="projects-chip" sx={{ mr: 1, mb: 1 }} />
                      )) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New Project</DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box sx={{ mt: 3 }}>
            {renderStepContent(activeStep)}
          </Box>
        </DialogContent>
        <DialogActions>
          {activeStep !== 0 && (
            <Button onClick={handleBack}>
              Back
            </Button>
          )}
          {activeStep === steps.length - 1 ? (
            <Button onClick={handleSubmit} color="primary" disabled={isNextDisabled()}>
              Submit
            </Button>
          ) : (
            <Button onClick={handleNext} color="primary" disabled={isNextDisabled()}>
              Next
            </Button>
          )}
          <Button onClick={() => setOpen(false)} color="secondary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Project Details Dialog */}
      <Dialog open={projectDetailsOpen} onClose={handleCloseProjectDetails} fullWidth maxWidth="md">
        {selectedProject && (
          <>
            <DialogTitle>
              {selectedProject.name}
              {!editMode && (
                <IconButton
                  aria-label="edit"
                  onClick={() => setEditMode(true)}
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                  <EditIcon />
                </IconButton>
              )}
            </DialogTitle>
            <DialogContent>
              {editMode ? (
                <Box>
                  <Typography variant="body1" color="textSecondary">
                    Editing of project name and scene description coming soon.
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography><strong>Scene Description:</strong> {selectedProject.scene_description}</Typography>
                  <Typography><strong>Status:</strong> <StatusLabel status={selectedProject.status} /></Typography>
                  <Typography><strong>Deadline:</strong> {new Date(selectedProject.deadline).toLocaleDateString()}</Typography>
                  <Typography><strong>Deepfake Type:</strong> {selectedProject.deepfake_type}</Typography>
                  <Typography><strong>Body Double Usage:</strong> {selectedProject.body_double_usage}</Typography>
                  <Typography><strong>Clip Length:</strong> {selectedProject.clip_length} minutes</Typography>
                  <Typography><strong>Audio Deepfake:</strong> {selectedProject.audio_deepfake}</Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              {editMode ? (
                <>
                  <Button onClick={() => setEditMode(false)}>Cancel</Button>
                </>
              ) : (
                <>
                  <Button onClick={handleCancelProject} color="secondary" startIcon={<CancelIcon />}>
                    Cancel Project
                  </Button>
                  <Button onClick={handleCloseProjectDetails}>Close</Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default Projects;
